// It's Me — the stage: one draw call of signed-distance type and shapes over a shader backdrop,
// then trails, bloom and the glitch finish. The director (direct.js) fills it every frame.
(function () {
  const H = window.HANA;
  const G = H.G = {};
  const MAX = 16000;

  let renderer, composer, bgScene, bgCam, scene, tscene, camera, bgMat, bloom, finish, trail;
  let W = 1, Hh = 1, dpr = 1;

  // ---- the instance buffer: center, two half-axes, atlas rect, colour, style, effects
  // two layers: the world (bloomed, trailed) and the type on top (crisp, drawn after the bloom and the trails)
  const mkBuf = () => ({
    C: new Float32Array(MAX * 3), X: new Float32Array(MAX * 3), Y: new Float32Array(MAX * 3),
    UV: new Float32Array(MAX * 4), COL: new Float32Array(MAX * 4), ST: new Float32Array(MAX * 4), FX: new Float32Array(MAX * 4),
    n: 0, attrs: {}, geo: null
  });
  const LAYERS = [mkBuf(), mkBuf()];
  let B = LAYERS[0], n = 0;
  G.count = () => LAYERS[0].n + LAYERS[1].n;
  G.begin = () => { LAYERS[0].n = 0; LAYERS[1].n = 0; B = LAYERS[0]; };
  G.layer = i => { const prev = LAYERS.indexOf(B); B = LAYERS[i]; return prev; };
  // kind: 0 glyph, 1 rect, 2 disc, 3 sparkle, 4 star, 5 heart, 6 glow stick, 7 triangle, 8 halftone patch, 9 soft disc, 10 gradient bar
  // line: outline width (glyph: in distance units, ~0.04–0.2; shapes: world units); glow: halo strength; add: 0 normal, 1 additive
  G.quad = function (cx, cy, cz, ax, ay, az, bx, by, bz, uv, r, g, b, a, kind, line, glow, add, f0, f1, f2, f3) {
    const n = B.n;
    if (n >= MAX || a <= 0.002) return;
    const i3 = n * 3, i4 = n * 4;
    B.C[i3] = cx; B.C[i3 + 1] = cy; B.C[i3 + 2] = cz;
    B.X[i3] = ax; B.X[i3 + 1] = ay; B.X[i3 + 2] = az;
    B.Y[i3] = bx; B.Y[i3 + 1] = by; B.Y[i3 + 2] = bz;
    if (uv) { B.UV[i4] = uv.u0; B.UV[i4 + 1] = uv.v0; B.UV[i4 + 2] = uv.u1; B.UV[i4 + 3] = uv.v1; }
    else { B.UV[i4] = 0; B.UV[i4 + 1] = 0; B.UV[i4 + 2] = 0; B.UV[i4 + 3] = 0; }
    B.COL[i4] = r; B.COL[i4 + 1] = g; B.COL[i4 + 2] = b; B.COL[i4 + 3] = a;
    B.ST[i4] = kind; B.ST[i4 + 1] = line || 0; B.ST[i4 + 2] = glow || 0; B.ST[i4 + 3] = add || 0;
    B.FX[i4] = f0 || 0; B.FX[i4 + 1] = f1 || 0; B.FX[i4 + 2] = f2 || 0; B.FX[i4 + 3] = f3 || 0;
    B.n++;
  };

  const VERT = /* glsl */`
attribute vec3 aC, aX, aY;
attribute vec4 aUV, aCol, aSt, aFx;
varying vec2 vUv, vQ, vSize;
varying vec4 vCol, vSt, vFx;
void main(){
  vQ = position.xy;
  vUv = mix(aUV.xy, aUV.zw, vec2(uv.x, 1. - uv.y));
  vSize = vec2(length(aX), length(aY));
  vCol = aCol; vSt = aSt; vFx = aFx;
  vec3 p = aC + aX * position.x + aY * position.y;
  gl_Position = projectionMatrix * viewMatrix * vec4(p, 1.);
}`;
  const FRAG = /* glsl */`
uniform sampler2D uAtlas;
uniform float uEdge, uT;
varying vec2 vUv, vQ, vSize;
varying vec4 vCol, vSt, vFx;
float h21(vec2 p){ return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }
float sdStar5(vec2 p, float r, float rf){
  const vec2 k1 = vec2(0.809016994375, -0.587785252292);
  const vec2 k2 = vec2(-k1.x, k1.y);
  p.x = abs(p.x);
  p -= 2.0 * max(dot(k1, p), 0.0) * k1;
  p -= 2.0 * max(dot(k2, p), 0.0) * k2;
  p.x = abs(p.x);
  p.y -= r;
  vec2 ba = rf * vec2(-k1.y, k1.x) - vec2(0, 1);
  float h = clamp(dot(p, ba) / dot(ba, ba), 0.0, r);
  return length(p - ba * h) * sign(p.y * ba.x - p.x * ba.y);
}
float sdHeart(vec2 p){
  p.x = abs(p.x);
  if (p.y + p.x > 1.0) return sqrt(dot(p - vec2(0.25, 0.75), p - vec2(0.25, 0.75))) - sqrt(2.0) / 4.0;
  return sqrt(min(dot(p - vec2(0.00, 1.00), p - vec2(0.00, 1.00)), dot(p - 0.5 * max(p.x + p.y, 0.0), p - 0.5 * max(p.x + p.y, 0.0)))) * sign(p.x - p.y);
}
float fill(float sd){ float w = max(fwidth(sd), 1e-5) * .7; return 1. - smoothstep(-w, w, sd); }
void main(){
  float kind = vSt.x;
  float a = 0., glow = 0.;
  vec3 col = vCol.rgb;
  if (kind < .5) {
    float d = texture2D(uAtlas, vUv).r + vFx.z;
    if (vFx.y > 0.) d += (h21(floor(vUv * 900.) + floor(uT * 30.)) - .5) * vFx.y;   // a torn, noisy edge
    float w = max(fwidth(d) * .7, .003);
    float e = uEdge;
    if (vSt.y > 0.) {
      float o = vSt.y;
      a = smoothstep(e - o - w, e - o + w, d) * (1. - smoothstep(e + o * .35 - w, e + o * .35 + w, d));
    } else a = smoothstep(e - w, e + w, d);
    glow = smoothstep(.05, e, d); glow *= glow;
  } else {
    vec2 p = vQ * vSize;
    float sd = 1.;
    if (kind < 1.5) { vec2 r = vSize - vFx.x; vec2 q = abs(p) - r; sd = length(max(q, 0.)) + min(max(q.x, q.y), 0.) - vFx.x; }
    else if (kind < 2.5) { sd = (length(vQ) - 1.) * min(vSize.x, vSize.y); }
    else if (kind < 3.5) { vec2 q = abs(vQ); sd = (sqrt(q.x) + sqrt(q.y) - 1.) * min(vSize.x, vSize.y) * .5; }
    else if (kind < 4.5) { sd = sdStar5(vQ * vec2(1., -1.) * 1.05 + vec2(0., -.05), .95, .45) * min(vSize.x, vSize.y); }
    else if (kind < 5.5) { sd = sdHeart(vQ * vec2(.62, .62) + vec2(0., .5)) * min(vSize.x, vSize.y); }
    else if (kind < 6.5) { a = pow(max(0., 1. - abs(vQ.x)), 2.) * smoothstep(1., .55, abs(vQ.y)); glow = 1.; }
    else if (kind < 7.5) { vec2 q = vQ; sd = max(abs(q.x) * .866 + q.y * .5, -q.y) - .5; sd *= min(vSize.x, vSize.y); }
    else if (kind < 8.5) {
      vec2 g = p / max(vFx.x, 1e-3);
      vec2 c = fract(mat2(.707, -.707, .707, .707) * g) - .5;
      float rad = .5 * clamp(vFx.y + vFx.w * vQ.y, 0., 1.);
      a = fill(length(c) - rad) * (1. - smoothstep(.9, 1., max(abs(vQ.x), abs(vQ.y))));
    }
    else if (kind < 9.5) { float r = length(vQ); a = pow(max(0., 1. - r), 1.6); glow = a; }
    else { a = smoothstep(-1., 1., vQ.y * vFx.x) * smoothstep(1., .6, abs(vQ.x)); }
    if (kind < 5.5 || (kind > 6.5 && kind < 7.5)) {
      if (vSt.y > 0.) sd = abs(sd + vSt.y * .5) - vSt.y * .5;
      a = fill(sd);
      glow = exp(-max(sd, 0.) / (.04 * min(vSize.x, vSize.y) + 1e-4));
    }
  }
  float A = vCol.a * a;
  vec3 C = col * A + col * vSt.z * glow * vCol.a * (1. - a);
  gl_FragColor = vec4(C, A * (1. - vSt.w));
}`;

  // ---- the backdrop: one shader, many worlds
  const BG_FRAG = /* glsl */`
uniform vec2 uRes;
uniform float uT, uMode, uKick, uBeat, uRoll, uZoom, uSeed, uAmt;
uniform vec3 uA, uB, uC;
uniform vec4 uP;
uniform sampler2D uTex;
varying vec2 vUv;
float h21(vec2 p){ return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }
float noise(vec2 p){ vec2 i = floor(p), f = fract(p); f = f * f * (3. - 2. * f);
  return mix(mix(h21(i), h21(i + vec2(1, 0)), f.x), mix(h21(i + vec2(0, 1)), h21(i + vec2(1, 1)), f.x), f.y); }
float fbm(vec2 p){ float s = 0., a = .5; for (int i = 0; i < 5; i++) { s += a * noise(p); p = p * 2.03 + 17.1; a *= .5; } return s; }
mat2 rot(float a){ float c = cos(a), s = sin(a); return mat2(c, -s, s, c); }
vec3 hue(float x){ return .5 + .5 * cos(6.2832 * (x + vec3(0., .33, .67))); }
float line(float d, float w){ return 1. - smoothstep(w * .5, w * .5 + 1.5 / uRes.y, abs(d)); }

vec3 stageLights(vec2 p, float t){
  vec3 c = mix(vec3(.02, .0, .05), uC * .25, smoothstep(-.6, .5, p.y));
  for (int i = 0; i < 6; i++) {
    float fi = float(i);
    vec2 src = vec2((fi - 2.5) * .34, .62);
    float ang = 3.1416 * 1.5 + sin(t * (.6 + fi * .13) + fi * 1.7) * .55;
    vec2 dir = vec2(cos(ang), sin(ang));
    vec2 d = p - src;
    float along = dot(d, dir);
    float across = abs(d.x * dir.y - d.y * dir.x);
    float wdt = .02 + along * .16;
    float beam = smoothstep(wdt, 0., across) * step(0., along) * exp(-along * 1.1);
    c += (mod(fi, 2.) < .5 ? uA : uB) * beam * (.55 + .45 * uKick) * uP.y;
  }
  c += vec3(.08, .03, .1) * fbm(p * 3. + vec2(t * .05, 0.)) * uP.y;
  return c;
}
vec3 penlights(vec2 p, vec3 c, float t){
  // three rows of a crowd holding penlights, swaying on the beat
  for (int r = 0; r < 3; r++) {
    float fr = float(r);
    float base = -.5 + fr * .11 + uP.z;
    float dens = 30. - fr * 7.;
    float x = p.x * dens + fr * 3.7;
    float id = floor(x);
    float fx = fract(x) - .5;
    float sway = sin(uBeat * 3.1416 + h21(vec2(id, fr)) * .6) * .45;
    float y = (p.y - base) * dens;
    float lx = fx - sway * y * .5;
    float stick = smoothstep(.13, 0., abs(lx)) * smoothstep(0., .2, y) * smoothstep(1.7, 1.2, y);
    float glow = exp(-abs(lx) * 5.) * smoothstep(-.3, .6, y) * smoothstep(2.3, .9, y) * .35;
    float pick = h21(vec2(id * 1.3, fr + 4.));
    vec3 pc = pick < .45 ? uA : pick < .8 ? uB : uC;
    float head = smoothstep(.42, .38, length(vec2(fx * 1.2, (y + .35) * .9)));
    float fade = .45 + fr * .25;
    c = mix(c, vec3(.015, .0, .03), head * step(y, 0.) * .9);
    c += pc * (stick * 1.4 + glow) * fade * uP.w;
  }
  return c;
}

void main(){
  vec2 p = (gl_FragCoord.xy - .5 * uRes) / uRes.y;
  p = rot(uRoll) * p / uZoom;
  float t = uT;
  int m = int(uMode + .5);
  vec3 c = uA;
  if (m == 0) {                       // void
    c = uA * (1. - .7 * length(p)) + (h21(gl_FragCoord.xy + fract(t) * 100.) - .5) * .03;
  } else if (m == 1) {                // pop: sunburst and halftone
    float ang = atan(p.y, p.x);
    float r = length(p);
    float ray = step(.5, fract(ang / 6.2832 * uP.x + t * uP.y));
    c = mix(uA, uA * .82 + uB * .18, ray);
    vec2 g = rot(.785) * p * 34.;
    float dotr = .5 * smoothstep(.15, .95, r) + .12 * uKick;
    float dt = 1. - smoothstep(dotr - .06, dotr + .06, length(fract(g) - .5));
    c = mix(c, uC, dt * .55);
    c *= 1. - .25 * smoothstep(.5, 1.1, r);
  } else if (m == 2) {                // stage: beams, haze, penlights
    c = stageLights(p, t);
    c = penlights(p, c, t);
  } else if (m == 3) {                // liquid paint smeared across the dark
    vec2 q = p * vec2(1.2, 2.6) + vec2(t * .05, 0.);
    for (int i = 0; i < 3; i++) q += .55 * vec2(fbm(q * 1.3 + t * .08 + float(i)), fbm(q * 1.3 + 5.2 - t * .07 + float(i)));
    float f = fbm(q * 1.7);
    float g = fbm(q * 2.3 + 9.);
    c = vec3(.025, .005, .035);
    c = mix(c, uC * .8, smoothstep(.48, .62, f) * .9);
    c = mix(c, uA, smoothstep(.6, .66, f) * smoothstep(.8, .66, f));
    c = mix(c, uB, smoothstep(.58, .64, g) * smoothstep(.72, .64, g) * .9);
    c += uA * pow(smoothstep(.62, .7, g), 3.) * .6;
    c *= .75 + .5 * uP.x;
  } else if (m == 4) {                // jewel box: facets of light
    vec2 q = p * 5.;
    q = rot(t * .05) * q;
    vec2 i = floor(q), f = fract(q);
    float d1 = 9., d2 = 9.; vec2 id = vec2(0.);
    for (int y = -1; y <= 1; y++) for (int x = -1; x <= 1; x++) {
      vec2 o = vec2(float(x), float(y));
      vec2 pt = o + .5 + .4 * sin(6.2832 * h21(i + o) + vec2(0., 1.57));
      float d = length(pt - f);
      if (d < d1) { d2 = d1; d1 = d; id = i + o; } else if (d < d2) d2 = d;
    }
    float edge = smoothstep(.06, .0, d2 - d1);
    float sh = h21(id);
    c = mix(uA, uB, sh) * (.35 + .65 * fract(sh * 7. + t * .3));
    c += hue(sh + t * .1 + length(p)) * .35;
    c += vec3(1.) * edge * .8;
    float sp = pow(max(0., sin(t * 3. + sh * 40.)), 40.);
    c += vec3(1.) * sp * smoothstep(.3, .0, d1) * 1.5;
    c *= 1. - .4 * length(p);
  } else if (m == 5) {                // white void with a rainbow halo
    c = mix(vec3(.97, .955, .975), vec3(.86, .84, .9), smoothstep(-.1, -.6, p.y));
    float r = length(p * vec2(1., 1.05));
    float R = uP.x;
    float ring = exp(-pow((r - R) / (.05 + .02 * uKick), 2.));
    vec3 rb = hue(.0 + (r - R) * 3.5 + .55);
    c = mix(c, rb * .9 + .15, ring * .8);
    c += exp(-pow((r - R) / .22, 2.)) * hue(.6 + (r - R) * 2.) * .08;
    c = mix(c, uA, uP.y * smoothstep(.0, .9, r) * .3);
  } else if (m == 6) {                // manga speed lines
    c = vec3(.97);
    float ang = atan(p.y, p.x);
    float r = length(p * vec2(.8, 1.));
    float N = 160.;
    float id = floor((ang / 6.2832 + .5) * N + floor(t * 15.) * 37.);
    float r0 = .18 + h21(vec2(id, floor(t * 15.))) * .32 + uP.x;
    float th = (fract((ang / 6.2832 + .5) * N) - .5);
    float w = .48 * smoothstep(r0, r0 + .5, r) * step(.4, h21(vec2(id, 3.)));
    float ln = smoothstep(w, w - .1, abs(th)) * step(r0, r);
    c = mix(c, uA, ln);
    vec2 g = rot(.6) * p * 70.;
    float dotr = .3 * smoothstep(.45, .95, r);
    c = mix(c, uB, (1. - smoothstep(dotr - .05, dotr + .05, length(fract(g) - .5))) * .8);
  } else if (m == 7) {                // the Sphere: an LED globe over the night
    c = mix(vec3(.01, .0, .04), vec3(.12, .02, .2), smoothstep(-.5, .3, p.y));
    // skyline
    float bx = floor(p.x * 18.);
    float bh = -.36 + h21(vec2(bx, 1.)) * .14;
    if (p.y < bh) { c = vec3(.01, .005, .02); vec2 wq = fract(p * vec2(140., 90.)); c += uB * .25 * step(.7, h21(floor(p * vec2(140., 90.)))) * step(.4, wq.x) * step(.4, wq.y); }
    vec2 sp = (p - vec2(0., uP.y)) / uP.x;
    float rr = dot(sp, sp);
    float glowR = exp(-max(0., sqrt(rr) - 1.) * 6.);
    c += uA * glowR * .25;
    if (rr < 1.) {
      vec3 nn = vec3(sp, sqrt(1. - rr));
      float lon = atan(nn.x, nn.z) + uP.z;
      float lat = asin(nn.y);
      vec2 su = vec2(lon / 3.1416, lat / 1.5708);
      vec2 grid = su * vec2(230., 110.);
      vec2 cell = floor(grid) + .5;
      vec2 tuv = vec2(fract(cell.x / 230. * .5 + .5 + .25), .5 + cell.y / 110. * 1.1);
      float txt = texture2D(uTex, tuv).r;
      vec3 led = mix(hue(cell.y * .02 - t * .3) * .35, uC, txt);
      led = mix(led, vec3(1., .9, .95), txt * uKick * .4);
      float dotm = smoothstep(.45, .2, length(fract(grid) - .5));
      float shade = .35 + .65 * nn.z;
      c = mix(vec3(.02, .0, .04), led * shade * 1.6, dotm) + led * .08;
    }
  } else if (m == 8) {                // paper
    c = uA * (.9 + .1 * fbm(p * 30.)) - .04 * step(.97, h21(floor(p * vec2(300., 4.))));
    c *= 1. - .3 * length(p);
  } else if (m == 9) {                // checker floor to a starry horizon
    float hor = .08;
    if (p.y < hor) {
      float z = .35 / (hor - p.y);
      float x = p.x * z;
      float zz = z + t * uP.x;
      float ch = mod(floor(x * 1.2) + floor(zz * 1.2), 2.);
      c = mix(uA, uB, ch);
      c = mix(c, uC, smoothstep(1.5, 9., z));
    } else {
      c = mix(uC, uC * .2, smoothstep(hor, .6, p.y));
      vec2 sg = floor(p * 60.);
      c += step(.985, h21(sg)) * (.5 + .5 * sin(t * 4. + h21(sg + 3.) * 30.)) * vec3(1.);
    }
  } else if (m == 10) {               // the mirror: frames inside frames, forever
    float asp = 1.45;
    float d = max(abs(p.x) / asp, abs(p.y)) + 1e-4;
    float l = log(d) / log(uP.x) + t * uP.y;
    float fl = fract(l);
    float idx = floor(l);
    c = mix(uA, uB, mod(idx, 2.));
    c = mix(c, uC, line(fl - .5, .06));
    c += vec3(1.) * line(fl - .02, .02) * .6;
    c *= .55 + .45 * smoothstep(0., .5, d);
  } else if (m == 11) {               // static
    float s = h21(floor(gl_FragCoord.xy / 2.) + floor(t * 30.) * 7.1);
    c = vec3(s) * .8;
    c *= .7 + .3 * sin(p.y * 300. + t * 50.);
  } else {                            // the feed: soft bokeh in the dark
    c = mix(vec3(.04, .01, .07), uC * .35, smoothstep(.8, -.6, p.y));
    for (int i = 0; i < 14; i++) {
      float fi = float(i);
      vec2 o = vec2(h21(vec2(fi, 1.)) - .5, fract(h21(vec2(fi, 2.)) + t * (.03 + .04 * h21(vec2(fi, 3.)))) - .5) * vec2(2., 1.4);
      float r = .05 + .12 * h21(vec2(fi, 4.));
      c += (mod(fi, 3.) < 1. ? uA : uB) * smoothstep(r, r * .6, length(p - o)) * .18;
    }
  }
  gl_FragColor = vec4(c * uAmt, 1.);
}`;

  // ---- the finish
  const FINISH = {
    uniforms: {
      tDiffuse: { value: null }, uRes: { value: new THREE.Vector2(1, 1) }, uT: { value: 0 }, uSeed: { value: 0 },
      uRGB: { value: 0 }, uSlice: { value: 0 }, uBlock: { value: 0 }, uFlash: { value: 0 }, uInv: { value: 0 },
      uScan: { value: 0.3 }, uGrain: { value: 0.06 }, uVig: { value: 0.3 }, uTint: { value: new THREE.Vector3(1, 1, 1) }, uDot: { value: 0 }, uDuo: { value: 0 },
      uPanels: { value: 0 }, uZB: { value: 0 }, uImpact: { value: 0 }, uBox: { value: 0 }
    },
    vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.); }',
    fragmentShader: /* glsl */`
uniform sampler2D tDiffuse; uniform vec2 uRes; uniform float uT, uSeed, uRGB, uSlice, uBlock, uFlash, uInv, uScan, uGrain, uVig, uDot, uDuo, uPanels, uZB, uImpact, uBox;
uniform vec3 uTint;
varying vec2 vUv;
float h(vec2 p){ return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }
void main(){
  vec2 q = vUv;
  // split screen: three mirrored strips, or the whole frame four times
  float pid = 0., divider = 0.;
  if (uPanels > 2.5 && uPanels < 3.5) {
    pid = floor(q.x * 3.);
    float fx = fract(q.x * 3.);
    if (mod(pid, 2.) > .5) fx = 1. - fx;
    q = vec2(.5 + (fx - .5) / 3., vUv.y);
    divider = step(abs(fract(vUv.x * 3. + .5) - .5), 1.5 / uRes.x);
  } else if (uPanels > 3.5) {
    vec2 g = floor(vUv * 2.);
    pid = g.x + g.y * 2.;
    q = fract(vUv * 2.);
    divider = step(min(abs(vUv.x - .5), abs(vUv.y - .5)), 1.5 / uRes.y);
  }
  // macroblocks knocked out of place
  vec2 bq = floor(q * vec2(24., 14.));
  float bh = h(bq + uSeed * 1.37);
  float blk = step(1. - uBlock * .35, bh);
  q += blk * (vec2(h(bq + uSeed + 1.), h(bq + uSeed + 2.)) - .5) * vec2(.2, .05);
  // torn scanline slices
  float band = floor(q.y * 48. + h(vec2(uSeed, 3.)) * 20.);
  float on = step(1. - uSlice * .5, h(vec2(band, uSeed)));
  q.x += on * (h(vec2(band, uSeed + 3.)) - .5) * .18;
  // colour split: sideways, wider toward the edges
  vec2 d = vec2(1., .15) * uRGB + (q - .5) * uRGB * .6;
  vec3 c = vec3(texture2D(tDiffuse, q + d).r, texture2D(tDiffuse, q).g, texture2D(tDiffuse, q - d).b);
  // zoom blur: the punch-in smears toward the centre
  if (uZB > 0.) {
    vec3 acc = c;
    for (int i = 1; i < 9; i++) { float k = float(i) / 8. * uZB; acc += texture2D(tDiffuse, q - (q - .5) * k).rgb; }
    c = acc / 9.;
  }
  // a panel of the grid goes negative on the beat
  if (uPanels > 3.5 && abs(pid - mod(floor(uT * 2.53), 4.)) < .5) c = vec3(1.) - c;
  // a block or two flips to its negative, or to one channel
  float flip = step(1. - uBlock * .08, h(bq + uSeed * 2.1));
  c = mix(c, vec3(1.) - c.gbr, flip);
  c = mix(c, vec3(1.) - c, uInv);
  c *= uTint;
  // the red-and-teal duotone of the music video
  if (uDuo > 0.) {
    float L = dot(c, vec3(.3, .59, .11));
    float warm = clamp(c.r - max(c.g, c.b) * .8, 0., 1.);
    vec3 d = mix(vec3(.03, .075, .08), vec3(.42, .5, .5), smoothstep(0., .55, L));
    d = mix(d, vec3(1., .94, .9), smoothstep(.55, .95, L));
    d = mix(d, vec3(1., .36, .32) * (.35 + L * .9), clamp(warm * 2.2, 0., 1.));
    c = mix(c, d, uDuo);
  }
  // datamosh: grey blocks left behind
  float gb = step(1. - uBlock * .06, h(floor(vUv * vec2(10., 7.)) + floor(uSeed / 3.) * 3.1));
  c = mix(c, vec3(.35, .37, .38) + (h(floor(gl_FragCoord.xy / 3.) + uSeed) - .5) * .15, gb * .85);
  // print halftone in the shadows
  if (uDot > 0.) {
    vec2 g = mat2(.707, -.707, .707, .707) * gl_FragCoord.xy / 5.;
    float L = dot(c, vec3(.3, .59, .11));
    float r = .5 * sqrt(clamp(1. - L * 1.6, 0., 1.));
    float dt = smoothstep(r + .08, r - .08, length(fract(g) - .5));
    c = mix(c, c * (1. - dt * .6), uDot);
  }
  // impact frames: a flat silhouette for a frame or two
  if (uImpact > .5) {
    float L = dot(c, vec3(.3, .59, .11));
    float m = smoothstep(.56, .66, L);
    if (uImpact < 1.5) c = mix(vec3(.93, .1, .16), vec3(.03, .0, .02), m);
    else c = mix(vec3(1., .97, .95), vec3(.03, .0, .02), m);
  }
  c += uFlash * (1. - step(.5, uImpact));
  c = mix(c, vec3(.02, .02, .025), divider);
  // letterbox
  float bar = step(.5 - uBox, abs(vUv.y - .5));
  float sl = .5 + .5 * sin(gl_FragCoord.y * 3.1416 * .5);
  c *= 1. - uScan * .22 * sl;
  vec2 v = vUv - .5;
  c *= 1. - uVig * smoothstep(.25, .85, length(v * vec2(1.2, 1.)));
  float g = h(floor(gl_FragCoord.xy / 1.5) + fract(uT * 7.31) * 113.) - .5;
  c += g * uGrain;
  c = mix(c, vec3(.0, .0, .005) + g * uGrain * .4, bar);
  gl_FragColor = vec4(c, 1.);
}`
  };

  // ---- trails: the last frames melt away along a flowing field (the "fluid")
  class TrailPass extends THREE.Pass {
    constructor() {
      super();
      const opt = { minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, type: THREE.HalfFloatType };
      this.a = new THREE.WebGLRenderTarget(4, 4, opt);
      this.b = new THREE.WebGLRenderTarget(4, 4, opt);
      this.mat = new THREE.ShaderMaterial({
        uniforms: { tCur: { value: null }, tPrev: { value: null }, uDecay: { value: 0 }, uFlow: { value: 0 }, uT: { value: 0 }, uZoom: { value: 0 }, uSpin: { value: 0 } },
        vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position.xy, 0., 1.); }',
        fragmentShader: /* glsl */`
uniform sampler2D tCur, tPrev; uniform float uDecay, uFlow, uT, uZoom, uSpin;
varying vec2 vUv;
float h21(vec2 p){ return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }
float noise(vec2 p){ vec2 i = floor(p), f = fract(p); f = f * f * (3. - 2. * f);
  return mix(mix(h21(i), h21(i + vec2(1, 0)), f.x), mix(h21(i + vec2(0, 1)), h21(i + vec2(1, 1)), f.x), f.y); }
void main(){
  vec4 cur = texture2D(tCur, vUv);
  vec2 c = vUv - .5;
  float a = uSpin; c = mat2(cos(a), -sin(a), sin(a), cos(a)) * c * (1. - uZoom);
  vec2 q = c + .5;
  vec2 fl = vec2(noise(vUv * 4. + uT * .3), noise(vUv * 4. + 7.3 - uT * .3)) - .5;
  q += fl * uFlow;
  vec4 prev = texture2D(tPrev, q) * uDecay;
  gl_FragColor = vec4(max(cur.rgb, prev.rgb), 1.);
}`
      });
      this.quad = new THREE.FullScreenQuad(this.mat);
      this.copy = new THREE.FullScreenQuad(new THREE.ShaderMaterial({
        uniforms: { t: { value: null } },
        vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position.xy, 0., 1.); }',
        fragmentShader: 'uniform sampler2D t; varying vec2 vUv; void main(){ gl_FragColor = texture2D(t, vUv); }'
      }));
    }
    setSize(w, h) { this.a.setSize(w, h); this.b.setSize(w, h); }
    render(r, writeBuffer, readBuffer) {
      this.mat.uniforms.tCur.value = readBuffer.texture;
      this.mat.uniforms.tPrev.value = this.a.texture;
      r.setRenderTarget(this.b); this.quad.render(r);
      [this.a, this.b] = [this.b, this.a];
      this.copy.material.uniforms.t.value = this.a.texture;
      r.setRenderTarget(this.renderToScreen ? null : writeBuffer); this.copy.render(r);
    }
  }

  // datamosh: after a cut the old picture keeps moving, block by block, along made-up motion
  class MoshPass extends THREE.Pass {
    constructor() {
      super();
      const opt = { minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, type: THREE.HalfFloatType };
      this.prev = new THREE.WebGLRenderTarget(4, 4, opt);
      this.mat = new THREE.ShaderMaterial({
        uniforms: { tCur: { value: null }, tPrev: { value: null }, uAmt: { value: 0 }, uSeed: { value: 0 }, uRes: { value: new THREE.Vector2(1, 1) } },
        vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position.xy, 0., 1.); }',
        fragmentShader: /* glsl */`
uniform sampler2D tCur, tPrev; uniform float uAmt, uSeed; uniform vec2 uRes;
varying vec2 vUv;
float h(vec2 p){ return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }
float lum(vec3 c){ return dot(c, vec3(.3, .59, .11)); }
void main(){
  vec4 cur = texture2D(tCur, vUv);
  if (uAmt <= 0.) { gl_FragColor = cur; return; }
  vec2 bq = floor(vUv * uRes / 24.);
  float on = step(1. - uAmt * .75, h(bq + floor(uSeed / 2.) * .37));
  vec2 px = 3. / uRes;
  vec2 gr = vec2(lum(texture2D(tCur, vUv + vec2(px.x, 0.)).rgb) - lum(texture2D(tCur, vUv - vec2(px.x, 0.)).rgb),
                 lum(texture2D(tCur, vUv + vec2(0., px.y)).rgb) - lum(texture2D(tCur, vUv - vec2(0., px.y)).rgb));
  vec2 mv = gr * .03 + (vec2(h(bq + 1.), h(bq + 2.)) - .5) * .025;
  vec4 old = texture2D(tPrev, vUv - mv);
  gl_FragColor = mix(cur, old, on);
}`
      });
      this.quad = new THREE.FullScreenQuad(this.mat);
      this.copy = new THREE.FullScreenQuad(new THREE.ShaderMaterial({
        uniforms: { t: { value: null } },
        vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position.xy, 0., 1.); }',
        fragmentShader: 'uniform sampler2D t; varying vec2 vUv; void main(){ gl_FragColor = texture2D(t, vUv); }'
      }));
    }
    setSize(w, h) { this.prev.setSize(w, h); this.mat.uniforms.uRes.value.set(w, h); }
    render(r, writeBuffer, readBuffer) {
      this.mat.uniforms.tCur.value = readBuffer.texture;
      this.mat.uniforms.tPrev.value = this.prev.texture;
      r.setRenderTarget(this.renderToScreen ? null : writeBuffer); this.quad.render(r);
      // what we showed becomes what the next frame drags around
      this.copy.material.uniforms.t.value = writeBuffer.texture;
      r.setRenderTarget(this.prev); this.copy.render(r);
    }
  }
  let mosh;

  G.init = function (canvas) {
    const coarse = matchMedia('(pointer: coarse)').matches || Math.min(innerWidth, innerHeight) < 600;
    renderer = new THREE.WebGLRenderer({ canvas, antialias: false, powerPreference: 'high-performance' });
    renderer.setClearColor(0x000000, 1);
    renderer.autoClear = false;
    G.renderer = renderer;
    G.bg = () => bgMat;

    bgScene = new THREE.Scene();
    bgCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    bgMat = new THREE.ShaderMaterial({
      uniforms: {
        uRes: { value: new THREE.Vector2(1, 1) }, uT: { value: 0 }, uMode: { value: 0 }, uKick: { value: 0 }, uBeat: { value: 0 },
        uRoll: { value: 0 }, uZoom: { value: 1 }, uSeed: { value: 0 }, uAmt: { value: 1 },
        uA: { value: new THREE.Vector3() }, uB: { value: new THREE.Vector3() }, uC: { value: new THREE.Vector3() }, uP: { value: new THREE.Vector4() },
        uTex: { value: null }
      },
      vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position.xy, 0., 1.); }',
      fragmentShader: BG_FRAG, depthTest: false, depthWrite: false
    });
    const bq = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), bgMat);
    bq.frustumCulled = false;
    bgScene.add(bq);

    scene = new THREE.Scene();
    tscene = new THREE.Scene();
    camera = new THREE.PerspectiveCamera(40, 1, 0.05, 200);
    G.camera = camera;
    G.D = 1 / Math.tan(20 * Math.PI / 180);      // at this distance the screen is 2 units tall

    // the type and shapes
    const A = H.Atlas;
    const tex = new THREE.DataTexture(A.data, A.W, A.H, THREE.RedFormat, THREE.UnsignedByteType);
    tex.minFilter = THREE.LinearFilter; tex.magFilter = THREE.LinearFilter; tex.generateMipmaps = false; tex.needsUpdate = true;
    const pl = new THREE.PlaneGeometry(2, 2);
    for (const Lr of LAYERS) {
      const geo = Lr.geo = new THREE.InstancedBufferGeometry();
      geo.index = pl.index; geo.attributes.position = pl.attributes.position; geo.attributes.uv = pl.attributes.uv;
      const add = (name, arr, size) => {
        const at = new THREE.InstancedBufferAttribute(arr, size);
        at.setUsage(THREE.DynamicDrawUsage);
        geo.setAttribute(name, at); Lr.attrs[name] = at;
      };
      add('aC', Lr.C, 3); add('aX', Lr.X, 3); add('aY', Lr.Y, 3); add('aUV', Lr.UV, 4); add('aCol', Lr.COL, 4); add('aSt', Lr.ST, 4); add('aFx', Lr.FX, 4);
      geo.instanceCount = 0;
    }
    const mat = new THREE.ShaderMaterial({
      uniforms: { uAtlas: { value: tex }, uEdge: { value: A.edge }, uT: { value: 0 } },
      vertexShader: VERT, fragmentShader: FRAG,
      transparent: true, depthTest: false, depthWrite: false,
      blending: THREE.CustomBlending, blendSrc: THREE.OneFactor, blendDst: THREE.OneMinusSrcAlphaFactor,
      blendSrcAlpha: THREE.OneFactor, blendDstAlpha: THREE.OneMinusSrcAlphaFactor,
      extensions: { derivatives: true }
    });
    G.mat = mat;
    LAYERS.forEach((Lr, i) => { const m = new THREE.Mesh(Lr.geo, mat); m.frustumCulled = false; (i ? tscene : scene).add(m); });

    composer = new THREE.EffectComposer(renderer);
    const bp = new THREE.RenderPass(bgScene, bgCam);
    composer.addPass(bp);
    const mp = new THREE.RenderPass(scene, camera); mp.clear = false;
    composer.addPass(mp);
    trail = new TrailPass(); composer.addPass(trail);
    bloom = new THREE.UnrealBloomPass(new THREE.Vector2(256, 256), 0.7, 0.5, 0.75);
    composer.addPass(bloom);
    const tp = new THREE.RenderPass(tscene, camera); tp.clear = false; tp.clearDepth = true;
    composer.addPass(tp);
    mosh = new MoshPass(); composer.addPass(mosh);
    finish = new THREE.ShaderPass(FINISH); composer.addPass(finish);
    G.dprMax = coarse ? 1.25 : 1.5;
    G.resize(); addEventListener('resize', G.resize);
  };

  G.resize = function () {
    dpr = Math.min(devicePixelRatio || 1, G.dprMax || 1.5);
    W = innerWidth; Hh = innerHeight;
    renderer.setPixelRatio(dpr); renderer.setSize(W, Hh, false);
    composer.setPixelRatio(dpr); composer.setSize(W, Hh);
    camera.aspect = W / Hh; camera.updateProjectionMatrix();
    bgMat.uniforms.uRes.value.set(W * dpr, Hh * dpr);
    finish.uniforms.uRes.value.set(W * dpr, Hh * dpr);
    G.aspect = W / Hh;
  };

  // a texture the Sphere shows (words drawn in 2D, red channel lit)
  let sphereCv = null, sphereTxt = null;
  G.sphereTex = function (text) {
    if (text === sphereTxt) return;
    sphereTxt = text;
    const old = bgMat.uniforms.uTex.value;
    const cv = sphereCv || (sphereCv = document.createElement('canvas')); cv.width = 1024; cv.height = 256;
    const cx = cv.getContext('2d');
    cx.fillStyle = '#000'; cx.fillRect(0, 0, 1024, 256);
    cx.fillStyle = '#fff'; cx.textAlign = 'center'; cx.textBaseline = 'middle';
    cx.font = '800 150px "Avenir Next Condensed", "Hiragino Kaku Gothic StdN", "Noto Sans JP", sans-serif';
    const fs = Math.min(150, 150 * 360 / cx.measureText(text).width);
    cx.font = '800 ' + fs + 'px "Avenir Next Condensed", "Hiragino Kaku Gothic StdN", "Noto Sans JP", sans-serif';
    cx.fillText(text, 256, 138); cx.fillText(text, 768, 138);
    if (old) { old.needsUpdate = true; return; }
    const t = new THREE.CanvasTexture(cv);
    t.wrapS = THREE.RepeatWrapping;
    bgMat.uniforms.uTex.value = t;
  };

  // st: { t, bg:{mode,a,b,c,p,kick,beat,roll,zoom,amt}, cam:{x,y,z,roll,fov,lx,ly}, post:{...}, trail:{decay,flow,zoom,spin}, bloom:{s,r,th} }
  G.render = function (st) {
    if (!renderer) return;
    for (const Lr of LAYERS) {
      for (const k in Lr.attrs) { const at = Lr.attrs[k]; at.updateRange.offset = 0; at.updateRange.count = Lr.n * at.itemSize; at.needsUpdate = true; }
      Lr.geo.instanceCount = Lr.n;
    }
    G.mat.uniforms.uT.value = st.t;
    const u = bgMat.uniforms, bg = st.bg;
    u.uT.value = st.t; u.uMode.value = bg.mode; u.uKick.value = bg.kick || 0; u.uBeat.value = bg.beat || 0;
    u.uRoll.value = bg.roll || 0; u.uZoom.value = bg.zoom || 1; u.uAmt.value = bg.amt == null ? 1 : bg.amt;
    u.uA.value.set(...bg.a); u.uB.value.set(...bg.b); u.uC.value.set(...bg.c); u.uP.value.set(...(bg.p || [0, 0, 0, 0]));
    const c = st.cam;
    camera.fov = c.fov || 40; camera.updateProjectionMatrix();
    camera.position.set(c.x || 0, c.y || 0, (c.z == null ? G.D : c.z));
    camera.up.set(Math.sin(c.roll || 0), Math.cos(c.roll || 0), 0);
    camera.lookAt(c.lx || 0, c.ly || 0, c.lz == null ? 0 : c.lz);
    const tr = trail.mat.uniforms, T = st.trail || {};
    tr.uDecay.value = T.decay || 0; tr.uFlow.value = T.flow || 0; tr.uT.value = st.t; tr.uZoom.value = T.zoom || 0; tr.uSpin.value = T.spin || 0;
    const bl = st.bloom || {};
    bloom.strength = bl.s == null ? 0.7 : bl.s; bloom.radius = bl.r == null ? 0.5 : bl.r; bloom.threshold = bl.th == null ? 0.75 : bl.th;
    const f = finish.uniforms, P = st.post || {};
    f.uT.value = st.t; f.uSeed.value = P.seed || 0; f.uRGB.value = P.rgb || 0; f.uSlice.value = P.slice || 0; f.uBlock.value = P.block || 0;
    f.uFlash.value = P.flash || 0; f.uInv.value = P.inv || 0; f.uScan.value = P.scan == null ? 0.3 : P.scan; f.uGrain.value = P.grain == null ? 0.06 : P.grain;
    f.uVig.value = P.vig == null ? 0.3 : P.vig; f.uDot.value = P.dot || 0; f.uDuo.value = P.duo || 0;
    f.uPanels.value = P.panels || 0; f.uZB.value = P.zb || 0; f.uImpact.value = P.impact || 0; f.uBox.value = P.box || 0;
    mosh.mat.uniforms.uAmt.value = P.mosh || 0; mosh.mat.uniforms.uSeed.value = P.seed || 0;
    f.uTint.value.set(...(P.tint || [1, 1, 1]));
    renderer.clear();
    composer.render();
  };
})();
